import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Headline from '../../ui/Headline';
import Button from '../../ui/Button';
import ButtonsGroup from '../../ui/ButtonsGroup';
import Input from '../../ui/Input';
import RichTextEditor from '../../ui/RichTextEditor';
import TranslatedFieldsRow from '../TranslationsFieldsRow';
import { useValidator, } from '../../hooks/formValidator/useValidator';
import { APP_LANGUAGES } from '../../app.config.vars/languages';
import { formatLangCode } from '../../helpers';
import { createFaqItem, updateFaqItem } from '../../api/admin/faq';
import { useAppDispatch } from '../../store';
import { setIsModalOpen, setModalContent } from '../../store/slices/global';
import { MODAL_CONTENT } from '../../app.config.vars/modalContent';
// Phase 6 D-08 + D-12 + D-19 + D-21 + UI-SPEC § "Inline edit form" (item variant):
//
// Inline create / edit form for a FAQ item. Two blocks per language: a plain
// localized question Input + a RichTextEditor answer with the FAQ-constrained
// toolbar (NO underline — Phase 5 D-13 sanitizer strips <u>).
//
// Validation (D-21):
//   * questionDE: required (trim, non-empty) + max 500 (SQL column).
//   * questionEN / questionCS: optional; if non-empty, max 500.
//   * answerDE: required after the empty-Jodit normalization idiom
//       value.replace(/<p><br><\/p>/gi, '').replace(/&nbsp;/g, '').trim()
//     to defeat Jodit's `<p><br></p>` "empty" placeholder content.
//   * answerEN / answerCS: no required check, no length cap (TEXT column).
//
// Payload (D-19):
//   * de always present.
//   * en / cs OMITTED entirely when both question AND answer trim to empty
//     (sent as absent keys, never as empty strings — backend interprets
//     absent as "no translation" per Phase 5 D-08).
//
// Note on TranslatedFieldsRow textarea={true}: WR-06 extended
// TranslatedFieldsRow with an `rteButtons` prop that forwards to each non-
// current-language RichTextEditor. We pass FAQ_RTE_BUTTONS (no `underline`)
// so all three language editors use the same toolbar — the previous footgun
// (Phase 5 sanitizer silently stripping <u> on save) is now closed at the UI
// layer too.
// Phase 5 SQL column constraint on tx_compass_domain_model_faqitem.question.
// Inline so a static grep for `maxLength: 500` finds the binding.
const MAX_QUESTION_LEN = 500; // maxLength: 500
const isQuestionRequired = (value) => value.trim().length > 0
    ? null
    : { messageTranslationKey: 'admin.faq.error.question_required' };
const questionMaxLength = (value) => value.length <= MAX_QUESTION_LEN
    ? null
    : {
        messageTranslationKey: 'admin.faq.error.question_too_long',
        variableToUseInMessage: { maxLength: MAX_QUESTION_LEN },
    };
const optionalQuestionMaxLength = (value) => !value
    ? null
    : value.length <= MAX_QUESTION_LEN
        ? null
        : {
            messageTranslationKey: 'admin.faq.error.question_too_long',
            variableToUseInMessage: { maxLength: MAX_QUESTION_LEN },
        };
// Empty-Jodit normalization (used for VALIDATION / emptiness check): strips
// Jodit's `<p><br></p>` placeholders and `&nbsp;` globally, then trims, so the
// validator can tell "this field is semantically empty" even when Jodit has
// inserted placeholder markup.
const normalizeAnswer = (value) => value
    .replace(/<p><br><\/p>/gi, '')
    .replace(/&nbsp;/g, '')
    .trim();
// Trailing-only Jodit normalization (used for PAYLOAD shipping): preserves
// meaningful markup inside the answer while stripping trailing empty
// paragraphs Jodit appends as the user types (`<p>Hello</p><p><br></p>`
// → `<p>Hello</p>`). Matches D-19 "no empty strings on the wire" without
// destroying internal HTML structure.
const trimTrailingEmptyParagraphs = (value) => value.replace(/(?:<p>(?:&nbsp;|\s|<br\s*\/?>)*<\/p>\s*)+$/gi, '').trimEnd();
const isAnswerRequired = (value) => normalizeAnswer(value).length > 0
    ? null
    : { messageTranslationKey: 'admin.faq.error.answer_required' };
const makePairValidator = (selfKind, companionKey) => {
    return (value, context) => {
        const companions = context ?? {};
        const companion = companions[companionKey] ?? '';
        const selfFilled = selfKind === 'question' ? value.trim().length > 0 : normalizeAnswer(value).length > 0;
        const companionFilled = selfKind === 'question'
            ? normalizeAnswer(companion).length > 0
            : companion.trim().length > 0;
        if (selfFilled !== companionFilled) {
            return { messageTranslationKey: 'admin.faq.error.pair_incomplete' };
        }
        return null;
    };
};
// One validator per non-DE field; each is hard-bound to the OTHER half of the
// language pair via the companion key.
const isQuestionENPairComplete = makePairValidator('question', 'answerEN');
const isQuestionCSPairComplete = makePairValidator('question', 'answerCS');
const isAnswerENPairComplete = makePairValidator('answer', 'questionEN');
const isAnswerCSPairComplete = makePairValidator('answer', 'questionCS');
const itemFormSchema = {
    questionDE: [isQuestionRequired, questionMaxLength],
    questionEN: [optionalQuestionMaxLength, isQuestionENPairComplete],
    questionCS: [optionalQuestionMaxLength, isQuestionCSPairComplete],
    answerDE: [isAnswerRequired],
    answerEN: [isAnswerENPairComplete],
    answerCS: [isAnswerCSPairComplete],
};
const FAQ_RTE_BUTTONS = ['bold', 'italic', 'ul', 'ol', 'link', 'eraser'];
const buildInitialQuestionValues = (item) => ({
    de: item?.translations.de?.question ?? '',
    en: item?.translations.en?.question ?? '',
    cs: item?.translations.cs?.question ?? '',
});
const buildInitialAnswerValues = (item) => ({
    de: item?.translations.de?.answer ?? '',
    en: item?.translations.en?.answer ?? '',
    cs: item?.translations.cs?.answer ?? '',
});
const FaqItemEditForm = ({ categoryUid, item, onCancel, onSaved, }) => {
    const { i18n, t } = useTranslation();
    const dispatch = useAppDispatch();
    const [questionValues, setQuestionValues] = useState(() => buildInitialQuestionValues(item));
    const [answerValues, setAnswerValues] = useState(() => buildInitialAnswerValues(item));
    const [submitStatus, setSubmitStatus] = useState(null);
    const { formErrors, validateAll } = useValidator({ inputSchema: itemFormSchema });
    // WR-04: normalize i18n.language to the supported union ('de'|'en'|'cs').
    // formatLangCode strips region tags; the APP_LANGUAGES guard falls back to
    // 'de' for any tag we do not support so the field-name derivation
    // (`question${currentLang.toUpperCase()}`) never produces a key absent
    // from the schema.
    const rawLang = formatLangCode(i18n.language || 'de');
    const currentLang = (APP_LANGUAGES.some((l) => l.code === rawLang)
        ? rawLang
        : 'de');
    const currentLangQuestionFieldName = `question${currentLang.toUpperCase()}`;
    const currentLangAnswerFieldName = `answer${currentLang.toUpperCase()}`;
    const handleCurrentLangQuestionChange = (e) => {
        const next = e.currentTarget.value;
        setQuestionValues((prev) => ({ ...prev, [currentLang]: next }));
    };
    const handleCurrentLangAnswerChange = (value) => {
        setAnswerValues((prev) => ({ ...prev, [currentLang]: value }));
    };
    const handleTranslatedQuestionChange = (lang, value) => {
        setQuestionValues((prev) => ({ ...prev, [lang]: value }));
    };
    const handleTranslatedAnswerChange = (lang, value) => {
        setAnswerValues((prev) => ({ ...prev, [lang]: value }));
    };
    // D-19 + Phase 6 CR-01/CR-02 fix: en/cs payload contract is a 3-way:
    //   * 'omit'        — return undefined; key absent in PATCH/POST body.
    //   * 'soft-delete' — return null; explicit `null` on PATCH so the server
    //                     soft-deletes the previously-existing localized row.
    //   * 'pair'        — return { question, answer } with TRIMMED question and
    //                     trailing-empty-stripped answer (preserves internal
    //                     markup, drops Jodit `<p><br></p>` placeholders).
    //   * 'partial'     — both fields are NOT both filled and NOT both empty;
    //                     the partial-pair error surfaces via the WR-03 paired
    //                     validator before this builder runs, so reaching this
    //                     state means validation was skipped — treated as omit
    //                     defensively.
    //
    // Mode rules:
    //   * Create mode (no `item` prop): both-blank → omit; mixed → omit
    //     defensively; both-filled → pair.
    //   * Update mode (`item` present): both-blank + previously-existed →
    //     soft-delete (null); both-blank + never-existed → omit; mixed → omit;
    //     both-filled → pair.
    const wasPresent = (lang) => {
        const tr = item?.translations[lang];
        if (!tr?.available)
            return false;
        // The server's `available` flag already encodes "row exists with non-
        // empty content"; the explicit `q && a` check is defensive.
        return Boolean(tr.question && tr.answer);
    };
    const buildLangPayload = (lang) => {
        const q = questionValues[lang].trim();
        const aNormalized = normalizeAnswer(answerValues[lang]);
        if (!q && !aNormalized) {
            // Both empty. Soft-delete if update mode and translation previously
            // existed; otherwise omit.
            if (item && wasPresent(lang))
                return { kind: 'soft-delete' };
            return { kind: 'omit' };
        }
        if (!q || !aNormalized) {
            // Partial pair — should be caught by the paired validator (WR-03)
            // before reaching here. Defensive omit if it slips through.
            return { kind: 'omit' };
        }
        // Both filled. Trim question; ship answer with trailing-empty paragraphs
        // stripped so D-19 "no empty strings on the wire" is satisfied while
        // preserving meaningful internal markup.
        return {
            kind: 'pair',
            question: q,
            answer: trimTrailingEmptyParagraphs(answerValues[lang]),
        };
    };
    const handleSave = async () => {
        // WR-03: pass non-DE companions in `context` so each paired validator can
        // look up its OTHER half by key (questionEN <-> answerEN, etc.). The
        // schema validators read `context.questionEN / .answerEN / ...` to
        // enforce "fill both or neither".
        const errors = validateAll({
            inputValues: {
                questionDE: questionValues.de,
                questionEN: questionValues.en,
                questionCS: questionValues.cs,
                answerDE: answerValues.de,
                answerEN: answerValues.en,
                answerCS: answerValues.cs,
            },
            context: {
                questionEN: questionValues.en,
                questionCS: questionValues.cs,
                answerEN: answerValues.en,
                answerCS: answerValues.cs,
            },
        });
        if (Object.keys(errors).length > 0) {
            return;
        }
        // CR-02: DE answer must also be trailing-empty-stripped before shipping
        // so Jodit's `<p><br></p>` placeholder never escapes onto the wire.
        const dePayload = {
            question: questionValues.de.trim(),
            answer: trimTrailingEmptyParagraphs(answerValues.de),
        };
        // IN-02: extract per-language results once instead of invoking the
        // builder twice per language.
        const enResult = buildLangPayload('en');
        const csResult = buildLangPayload('cs');
        const langSlot = (lang, result) => {
            if (result.kind === 'pair')
                return { [lang]: { question: result.question, answer: result.answer } };
            if (result.kind === 'soft-delete')
                return { [lang]: null };
            return {};
        };
        const translations = {
            de: dePayload,
            ...langSlot('en', enResult),
            ...langSlot('cs', csResult),
        };
        const payload = { translations };
        setSubmitStatus('pending');
        try {
            const saved = item
                ? await updateFaqItem(item.uid, payload)
                : await createFaqItem(categoryUid, payload);
            setSubmitStatus('success');
            onSaved(saved);
        }
        catch {
            // UI-SPEC § State Contract → on error, KEEP the form open with values
            // intact and surface the generic error modal (D-23).
            setSubmitStatus(null);
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
        }
    };
    const currentLangQuestionError = formErrors[currentLangQuestionFieldName];
    const currentLangAnswerError = formErrors[currentLangAnswerFieldName];
    return (_jsxs("div", { className: "faq-edit-form", children: [_jsx(Headline, { title: t(item ? 'admin.faq.edit_item' : 'admin.faq.add_item'), type: "h3" }), _jsx(Input, { id: `faq-item-question-${currentLang}`, label: t('admin.faq.label.item_question'), placeholder: t('admin.faq.placeholder.item_question'), value: questionValues[currentLang], onChange: handleCurrentLangQuestionChange, error: !!currentLangQuestionError?.length, errorMessage: currentLangQuestionError?.length
                    ? t(currentLangQuestionError[0].messageTranslationKey, currentLangQuestionError[0].variableToUseInMessage)
                    : undefined }), _jsx(TranslatedFieldsRow, { fieldName: "question", values: questionValues, onChange: handleTranslatedQuestionChange, formErrors: formErrors, placeholder: t('admin.faq.placeholder.item_question'), originContent: questionValues[currentLang] || '', isDataReady: true }), _jsx(RichTextEditor, { id: `faq-item-answer-${currentLang}`, label: t('admin.faq.label.item_answer'), placeholder: t('admin.faq.placeholder.item_answer'), value: answerValues[currentLang], onChange: handleCurrentLangAnswerChange, buttons: FAQ_RTE_BUTTONS, error: !!currentLangAnswerError?.length, errorMessage: currentLangAnswerError?.length
                    ? t(currentLangAnswerError[0].messageTranslationKey, currentLangAnswerError[0].variableToUseInMessage)
                    : undefined, isDataReady: true }), _jsx(TranslatedFieldsRow, { fieldName: "answer", textarea: true, values: answerValues, onChange: handleTranslatedAnswerChange, formErrors: formErrors, placeholder: t('admin.faq.placeholder.item_answer'), originContent: answerValues[currentLang] || '', isDataReady: true, rteButtons: FAQ_RTE_BUTTONS }), _jsxs(ButtonsGroup, { children: [_jsx(Button, { label: t('button.save'), onClick: handleSave, disabled: submitStatus === 'pending' || submitStatus === 'success' }), _jsx(Button, { variant: "outlined", label: t('button.cancel'), onClick: onCancel, disabled: submitStatus === 'pending' || submitStatus === 'success' })] })] }));
};
export default FaqItemEditForm;
// Exported for greppable verification — the FAQ-constrained toolbar
// (no `underline`) is the binding contract with the Phase 5 D-13 sanitizer.
export { FAQ_RTE_BUTTONS };
